import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import {
  ArrowUp, ArrowDown, Minus, ArrowLeft,
  Building2, Check, ChevronRight,
  Eye, Loader2, Lock, LogOut,
  Search, Shield, BarChart3,
  AlertTriangle, Sun, Moon,
} from "lucide-react";
import { useTheme } from "next-themes";

const B = process.env.REACT_APP_BACKEND_URL;

const RC = {
  green: { bg: "bg-emerald-500/10", text: "text-emerald-500", border: "border-emerald-500/20", fill: "#10b981" },
  yellow: { bg: "bg-amber-500/10", text: "text-amber-500", border: "border-amber-500/20", fill: "#f59e0b" },
  red: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20", fill: "#ef4444" },
};

const DirIcon = ({ dir }) => dir === "up" ? <ArrowUp className="h-3.5 w-3.5 text-emerald-500" /> : dir === "down" ? <ArrowDown className="h-3.5 w-3.5 text-red-400" /> : <Minus className="h-3.5 w-3.5 text-foreground/30" />;

/* ════ LOGIN ════ */
const NJEALogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${B}/api/njea/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || "Login failed"); }
      const data = await r.json();
      onLogin(data.user);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border border-border bg-card">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="h-8 w-8 overflow-hidden rounded-md"><img src="/brand-mark.png" alt="" className="h-full w-full object-cover" /></div>
              <span className="text-xs text-foreground/40 font-medium">589 Labs</span>
            </div>
            <Eye className="h-10 w-10 text-blue-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-foreground">NJEA Oversight Portal</h1>
            <p className="text-sm text-foreground/40 mt-1">Read-only access to school diagnostics</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-foreground/50 mb-1.5 block">Email</label>
              <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="your@njea.org" className="h-11 border-border bg-foreground/5 text-foreground" data-testid="njea-email" autoFocus />
            </div>
            <div>
              <label className="text-xs text-foreground/50 mb-1.5 block">Password</label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" className="h-11 border-border bg-foreground/5 text-foreground" data-testid="njea-password" />
            </div>
            {error && <div className="text-xs text-red-400 bg-red-950/30 border border-red-800/30 rounded-lg p-2.5">{error}</div>}
            <Button type="submit" disabled={loading || !email || !password} className="w-full h-11 rounded-lg bg-blue-600 text-white hover:bg-blue-600/90 font-semibold" data-testid="njea-login-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}Sign In
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

/* ════ HEADER ════ */
const NJEAHeader = ({ user, onSignOut }) => {
  const { theme, setTheme } = useTheme();
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-2xl">
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 flex items-center justify-between py-2.5">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-full border border-border bg-foreground/5 px-2.5 py-1">
            <div className="h-4 w-4 overflow-hidden rounded-sm"><img src="/brand-mark.png" alt="" className="h-full w-full object-cover" /></div>
            <span className="text-[10px] text-foreground/40 font-medium">589 Labs</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-semibold text-foreground">NJEA Oversight</span>
          </div>
          <Badge className="bg-blue-500/10 text-blue-400 text-[10px]">READ-ONLY</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground/50">{user?.full_name}</span>
          <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="h-8 w-8 rounded-full border border-border bg-secondary/50 flex items-center justify-center text-foreground/60">
            {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          </button>
          <Button variant="ghost" size="sm" onClick={onSignOut} className="text-foreground/50 text-xs"><LogOut className="h-3 w-3 mr-1" />Sign Out</Button>
        </div>
      </div>
    </header>
  );
};

/* ════ SCHOOL LIST ════ */
const SchoolList = ({ user, onSelectSchool }) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("school_name");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    fetch(`${B}/api/njea/schools?njea_user_id=${user?.id || ""}`)
      .then(r => r.json())
      .then(d => setSchools(d.schools || []))
      .catch(() => toast.error("Failed to load schools"))
      .finally(() => setLoading(false));
  }, [user]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const filtered = schools
    .filter(s => !search || s.school_name.toLowerCase().includes(search.toLowerCase()) || s.review_period.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-foreground/30" /></div>;

  const SortHeader = ({ label, field, className = "" }) => (
    <button onClick={() => toggleSort(field)} className={`text-left text-[10px] uppercase tracking-wider text-foreground/50 font-semibold hover:text-foreground transition-colors ${className}`}>
      {label} {sortKey === field && (sortDir === "asc" ? "\u2191" : "\u2193")}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="njea-school-list">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-foreground">School Oversight Dashboard</h1>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground/30" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search schools..." className="h-9 pl-9 w-64 text-sm" data-testid="njea-search" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="border-border/60 bg-card p-8 text-center">
          <Building2 className="h-10 w-10 text-foreground/15 mx-auto mb-3" />
          <p className="text-sm text-foreground/40">No schools with completed diagnostics found.</p>
        </Card>
      ) : (
        <Card className="border-border/60 bg-card overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 p-3 border-b border-border/60 bg-foreground/[0.02]">
            <SortHeader label="School" field="school_name" className="col-span-3" />
            <SortHeader label="Score" field="current_overall_score" className="col-span-1" />
            <SortHeader label="Risk" field="current_risk_level" className="col-span-1" />
            <div className="col-span-3 text-[10px] uppercase tracking-wider text-foreground/50 font-semibold">Pillar Scores</div>
            <SortHeader label="Prev" field="previous_overall_score" className="col-span-1" />
            <SortHeader label="Trend" field="score_change" className="col-span-1" />
            <SortHeader label="Period" field="review_period" className="col-span-1" />
            <div className="col-span-1 text-[10px] uppercase tracking-wider text-foreground/50 font-semibold">Date</div>
          </div>

          {/* Rows */}
          {filtered.map(s => {
            const c = RC[s.current_risk_band] || RC.red;
            return (
              <div key={s.tenant_id}
                className="grid grid-cols-12 gap-2 p-3 border-b border-border/30 hover:bg-foreground/[0.02] cursor-pointer transition-colors items-center"
                onClick={() => onSelectSchool(s)}
                data-testid={`njea-school-row-${s.tenant_id}`}
              >
                <div className="col-span-3 flex items-center gap-2 min-w-0">
                  <Building2 className="h-4 w-4 text-foreground/30 shrink-0" />
                  <span className="text-sm font-medium text-foreground truncate">{s.school_name}</span>
                </div>
                <div className="col-span-1">
                  <span className={`text-lg font-bold ${c.text}`}>{s.current_overall_score}</span>
                </div>
                <div className="col-span-1">
                  <Badge className={`${c.bg} ${c.text} text-[9px]`}>{s.current_risk_level}</Badge>
                </div>
                <div className="col-span-3 flex gap-1">
                  {Object.entries(s.current_pillar_scores || {}).slice(0, 7).map(([pk, score]) => {
                    const pc = score >= 80 ? RC.green : score >= 60 ? RC.yellow : RC.red;
                    return <span key={pk} className={`text-[9px] font-medium px-1 py-0.5 rounded ${pc.bg} ${pc.text}`} title={pk}>{Math.round(score)}</span>;
                  })}
                </div>
                <div className="col-span-1 text-sm text-foreground/50">
                  {s.previous_overall_score !== null ? s.previous_overall_score : "—"}
                </div>
                <div className="col-span-1 flex items-center gap-1">
                  <DirIcon dir={s.trend_direction} />
                  {s.score_change !== 0 && <span className="text-xs">{s.score_change > 0 ? "+" : ""}{s.score_change}</span>}
                </div>
                <div className="col-span-1 text-xs text-foreground/50">{s.review_period}</div>
                <div className="col-span-1 flex items-center justify-between">
                  <span className="text-[10px] text-foreground/30">{s.completion_date?.slice(0, 10)}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-foreground/20" />
                </div>
              </div>
            );
          })}
        </Card>
      )}

      <div className="text-[10px] text-foreground/30 text-center">{filtered.length} school{filtered.length !== 1 ? "s" : ""} with completed MySchoolRx diagnostics</div>
    </div>
  );
};

/* ════ SCHOOL SUMMARY ════ */
const SchoolSummary = ({ school, user, onBack, onDrillDown }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${B}/api/njea/schools/${school.tenant_id}/summary?njea_user_id=${user?.id || ""}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => toast.error("Failed to load summary"))
      .finally(() => setLoading(false));
  }, [school, user]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-foreground/30" /></div>;
  if (!data) return null;

  const snap = data.latest_snapshot;
  const oc = snap ? (RC[snap.overall_band] || RC.red) : RC.red;

  return (
    <div className="space-y-6" data-testid="njea-school-summary">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-foreground/60"><ArrowLeft className="h-4 w-4 mr-1" />All Schools</Button>
        <div>
          <h2 className="text-lg font-bold text-foreground">{data.school?.name || school.school_name}</h2>
          <p className="text-xs text-foreground/40">{data.school?.industry} &middot; {data.total_reviews} completed review{data.total_reviews !== 1 ? "s" : ""}</p>
        </div>
      </div>

      {/* Latest Snapshot Score */}
      {snap && (
        <Card className={`border ${oc.border} ${oc.bg}`}>
          <CardContent className="p-5 flex flex-col sm:flex-row items-center gap-5">
            <div className="flex flex-col items-center">
              <div className={`text-4xl font-bold ${oc.text}`}>{snap.overall_score}</div>
              <div className="text-[10px] text-foreground/40">out of 100</div>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <Badge className={`${oc.bg} ${oc.text} text-sm px-3 py-0.5 mb-1`}>{snap.overall_band_label}</Badge>
              <h3 className="text-lg font-bold text-foreground">{snap.cycle_label}</h3>
              <p className="text-xs text-foreground/50">Locked {snap.locked_at?.slice(0, 10)} &middot; {snap.total_questions_answered}/{snap.total_questions} questions</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => onDrillDown(snap.id)} className="shrink-0" data-testid="njea-drilldown-btn">
              <Eye className="h-3.5 w-3.5 mr-1" />View Responses
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Pillar Scores */}
      {snap && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[var(--brand)]" />Pillar Scores</h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(snap.pillar_scores || {}).map(([pk, ps]) => {
              const c = RC[ps.band] || RC.red;
              return (
                <Card key={pk} className={`border ${c.border}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between mb-1">
                      <span className="text-xs font-semibold text-foreground">{ps.label}</span>
                      <Badge className={`${c.bg} ${c.text} text-[9px]`}>{ps.band_label}</Badge>
                    </div>
                    <div className={`text-2xl font-bold ${c.text}`}>{ps.score}</div>
                    <div className="mt-1 h-1.5 rounded-full bg-foreground/10"><div className="h-full rounded-full" style={{ width: `${ps.score}%`, backgroundColor: c.fill }} /></div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Quarterly Trend */}
      {data.quarterly_trend?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Quarterly Trend</h3>
          <div className="space-y-1.5">
            {data.quarterly_trend.map((t, i) => {
              const c = RC[t.overall_band] || RC.red;
              return (
                <Card key={i} className={`border ${c.border} cursor-pointer hover:border-blue-500/30`} onClick={() => t.snapshot_id && onDrillDown(t.snapshot_id)}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`text-xl font-bold ${c.text}`}>{t.overall_score}</span>
                      <div><span className="text-sm text-foreground">{t.cycle_label}</span><span className="text-[10px] text-foreground/30 ml-2">{t.locked_at?.slice(0, 10)}</span></div>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.direction !== "baseline" && <><DirIcon dir={t.direction} /><span className="text-xs">{t.score_change > 0 ? "+" : ""}{t.score_change}</span></>}
                      <ChevronRight className="h-3.5 w-3.5 text-foreground/20" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Plan Progress */}
      {data.action_plan_stats && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Corrective Action Progress</h3>
          <div className="grid grid-cols-4 gap-2">
            {[["Total", data.action_plan_stats.total, "text-foreground"], ["Complete", data.action_plan_stats.complete, "text-emerald-500"], ["Verified", data.action_plan_stats.verified, "text-teal-500"], ["Overdue", data.action_plan_stats.overdue, "text-red-400"]].map(([l, v, c]) => (
              <Card key={l} className="border-border/60"><CardContent className="p-3 text-center"><div className={`text-lg font-bold ${c}`}>{v}</div><div className="text-[10px] text-foreground/40">{l}</div></CardContent></Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ════ RESPONSE DRILLDOWN ════ */
const ResponseDrillDown = ({ tenantId, snapshotId, user, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${B}/api/njea/schools/${tenantId}/responses/${snapshotId}?njea_user_id=${user?.id || ""}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => toast.error("Failed to load responses"))
      .finally(() => setLoading(false));
  }, [tenantId, snapshotId, user]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-foreground/30" /></div>;
  if (!data) return null;

  const responses = data.responses_snapshot || [];

  return (
    <div className="space-y-4" data-testid="njea-responses">
      <Button variant="ghost" size="sm" onClick={onBack} className="text-foreground/60"><ArrowLeft className="h-4 w-4 mr-1" />Back to Summary</Button>
      <h2 className="text-base font-bold text-foreground">{data.cycle_label} — Q&A Responses</h2>
      <div className="text-xs text-foreground/40">Locked {data.locked_at?.slice(0, 10)} &middot; {responses.length} responses</div>
      <div className="space-y-2">
        {responses.map(r => (
          <Card key={r.question_id || r.id} className="border-border/60">
            <CardContent className="p-3 flex items-start gap-3">
              <span className="text-[10px] text-foreground/30 font-mono shrink-0 w-10 mt-0.5">{r.question_id}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-[9px]">{r.pillar}</Badge>
                  {r.approval_status && <Badge className={r.approval_status === "overridden" ? "bg-amber-500/10 text-amber-500 text-[9px]" : "bg-emerald-500/10 text-emerald-500 text-[9px]"}>{r.approval_status}</Badge>}
                </div>
                <div className="text-sm text-foreground/70">Value: <span className="font-semibold text-foreground">{r.response_value}</span></div>
                {r.comment && <div className="text-xs text-foreground/40 mt-1">Comment: {r.comment}</div>}
                {r.created_by_name && <div className="text-[10px] text-foreground/25 mt-1">Submitted by {r.created_by_name} &middot; {r.created_at?.slice(0, 10)}</div>}
              </div>
            </CardContent>
          </Card>
        ))}
        {responses.length === 0 && <p className="text-sm text-foreground/40 text-center py-6">No response data in this snapshot.</p>}
      </div>
    </div>
  );
};

/* ════ MAIN NJEA PORTAL ════ */
export default function NJEAPortal() {
  const [user, setUser] = useState(() => {
    try { const s = sessionStorage.getItem("njea_session"); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [view, setView] = useState("list"); // list | summary | responses
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  const handleLogin = (u) => {
    setUser(u);
    try { sessionStorage.setItem("njea_session", JSON.stringify(u)); } catch {}
  };

  const handleSignOut = () => {
    setUser(null);
    try { sessionStorage.removeItem("njea_session"); } catch {}
    setView("list");
  };

  if (!user) return <NJEALogin onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-background" data-testid="njea-portal">
      <NJEAHeader user={user} onSignOut={handleSignOut} />
      <main className="mx-auto max-w-[1600px] px-6 py-8 sm:px-10">
        {view === "list" && (
          <SchoolList user={user} onSelectSchool={(s) => { setSelectedSchool(s); setView("summary"); }} />
        )}
        {view === "summary" && selectedSchool && (
          <SchoolSummary
            school={selectedSchool}
            user={user}
            onBack={() => { setView("list"); setSelectedSchool(null); }}
            onDrillDown={(snapId) => { setSelectedSnapshot(snapId); setView("responses"); }}
          />
        )}
        {view === "responses" && selectedSchool && selectedSnapshot && (
          <ResponseDrillDown
            tenantId={selectedSchool.tenant_id}
            snapshotId={selectedSnapshot}
            user={user}
            onBack={() => { setView("summary"); setSelectedSnapshot(null); }}
          />
        )}
      </main>
    </div>
  );
}
